import { Tab, Tabs } from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { FiMessageCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import FeedsModals from "../../component/feeds/feedsmodals";
import PostCard from "../../component/feeds/postcard";
import {
  CommentResponseDto,
  CommonProfileDto,
  EditingPost,
  PostResponseDto,
} from "../../types/types";
import { FeedsFilters } from "./feedsFiltersProps";

interface FeedsFilterProps {
  activeTab: "all" | "my";
  handleTabChange: (key: React.Key) => void;
  t: ReturnType<typeof useTranslation<"feeds">>["t"];
  filterProps: any;
}

export const FeedsFilter: React.FC<FeedsFilterProps> = ({ activeTab, handleTabChange, t, filterProps }) => {
  return (
    <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-2 flex-shrink-0">
      <div className="max-w-3xl mx-auto">
        <Tabs
          selectedKey={activeTab}
          onSelectionChange={handleTabChange}
          aria-label={t("feeds_tabs")}
          className="mb-2"
        >
          <Tab key="all" title={t("allPosts")} />
          <Tab key="my" title={t("myPosts")} />
        </Tabs>
        <FeedsFilters {...filterProps} />
      </div>
    </div>
  );
};

interface FeedsContentProps {
  loading: boolean;
  posts: PostResponseDto[];
  activeTab: "all" | "my";
  currentUserId: string | number;
  topProfiles: CommonProfileDto[];
  likedPosts: { [postId: number]: boolean };
  comments: { [postId: number]: CommentResponseDto[] };
  newComment: { [postId: number]: string };
  expandedComments: { [postId: number]: boolean };
  currentMediaIndex: { [postId: number]: number };
  editingPost: EditingPost | null;
  showOptionsMenu: number | null;
  showLikesModal: number | null;
  loadingLikes: boolean;
  likesData: any[];
  deleteModalPostId: number | null;
  reportModalPostId: number | null;
  reportReason: string;
  t: ReturnType<typeof useTranslation<"feeds">>["t"];
  onLike: (postId: number, isLiked: boolean) => Promise<void>;
  onShowLikes: (postId: number) => Promise<void>;
  onCloseLikesModal: () => void;
  onEdit: (post: PostResponseDto) => void;
  onDelete: () => void;
  onSavePost: (postId: number) => Promise<void>;
  onAddComment: (e: React.FormEvent, postId: number) => Promise<void>;
  onDeleteComment: (postId: number, commentId: number) => Promise<void>;
  onToggleComments: (postId: number) => Promise<void>;
  onSetNewComment: React.Dispatch<React.SetStateAction<{ [postId: number]: string }>>;
  onSetShowOptionsMenu: React.Dispatch<React.SetStateAction<number | null>>;
  onSetEditingPost: React.Dispatch<React.SetStateAction<EditingPost | null>>;
  onRemoveSingleMedia: (index: number) => void;
  onSetCurrentMediaIndex: React.Dispatch<React.SetStateAction<{ [postId: number]: number }>>;
  onCloseDeleteModal: () => void;
  onConfirmDelete: (postId: number) => Promise<void>;
  onOpenReportModal: (postId: number) => void;
  onCloseReportModal: () => void;
  onSetReportReason: React.Dispatch<React.SetStateAction<string>>;
  onConfirmReport: (postId: number) => Promise<void>;
}

export const FeedsContent: React.FC<FeedsContentProps> = ({
  loading,
  posts,
  activeTab,
  topProfiles,
  currentUserId,
  likedPosts,
  comments,
  newComment,
  expandedComments,
  currentMediaIndex,
  editingPost,
  showOptionsMenu,
  showLikesModal,
  loadingLikes,
  likesData,
  deleteModalPostId,
  reportModalPostId,
  reportReason,
  t,
  onLike,
  onShowLikes,
  onCloseLikesModal,
  onEdit,
  onDelete,
  onSavePost,
  onAddComment,
  onDeleteComment,
  onToggleComments,
  onSetNewComment,
  onSetShowOptionsMenu,
  onSetEditingPost,
  onRemoveSingleMedia,
  onSetCurrentMediaIndex,
  onCloseDeleteModal,
  onConfirmDelete,
  onOpenReportModal,
  onCloseReportModal,
  onSetReportReason,
  onConfirmReport,
}) => {
  const navigate = useNavigate();
  const handleViewProfile = (profileId?: string | number | null) => {
    if (!profileId) return;
    navigate(`/profile?profileId=${profileId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50" aria-live="polite">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-600 font-medium">{t("loadingPosts")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
{activeTab !== "my" && (
<div className="bg-white rounded-xl shadow-sm border p-4 mb-6 relative z-0">
 <h2 className="text-base font-semibold text-gray-700 mb-4">
  {t("topProfiles")}
</h2>
  {topProfiles.length === 0 ? (
  <div className="flex flex-col items-center justify-center py-10 text-center text-gray-500">
    <p className="text-sm font-medium">{t("noDataForSelectedFilters")}</p>
    <p className="text-xs mt-1">
  {t("noDataSuggestion")}
</p>
  </div>
) : (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
      {topProfiles.slice(0, 5).map((profile) => (
        <button
          type="button"
          key={profile.id}
          onClick={() => handleViewProfile(profile.id)}
          className="p-2 bg-gray-50 rounded-lg shadow-sm flex items-center space-x-2 hover:shadow-md transition-all w-full overflow-hidden text-left outline-none focus:outline-none focus:ring-0"
          aria-label={`${t("viewProfile", "View profile")} ${profile.firstName} ${profile.lastName}`}
        >
          <div className="relative flex-shrink-0">
            {profile.profileImage ? (
              <img
                src={profile.profileImage}
                alt={`${profile.firstName} ${profile.lastName}`}
                className="w-10 h-10 rounded-full object-cover border"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center text-white font-semibold text-sm border">
                {`${profile.firstName?.[0]?.toUpperCase() || ""}${profile.lastName?.[0]?.toUpperCase() || ""}`}
              </div>
            )}

            {profile.verified && (
              <span className="absolute -bottom-0 -right-0 bg-white w-3.5 h-3.5 rounded-full overflow-hidden">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 48 48"
                  className="w-full h-full"
                >
                  <polygon
                    fill="#42a5f5"
                    points="29.62,3 33.053,8.308 39.367,8.624 39.686,14.937 44.997,18.367 42.116,23.995 45,29.62 39.692,33.053 39.376,39.367 33.063,39.686 29.633,44.997 24.005,42.116 18.38,45 14.947,39.692 8.633,39.376 8.314,33.063 3.003,29.633 5.884,24.005 3,18.38 8.308,14.947 8.624,8.633 14.937,8.314 18.367,3.003 23.995,5.884"
                  />
                  <polygon
                    fill="#fff"
                    points="21.396,31.255 14.899,24.76 17.021,22.639 21.428,27.046 30.996,17.772 33.084,19.926"
                  />
                </svg>
              </span>
            )}
          </div>

          <div className="flex flex-col items-start min-w-0">
            <p className="text-xs font-medium leading-tight text-gray-800 truncate w-full">
              {profile.firstName} {profile.lastName}
            </p>
           <span className="text-[9px] px-2 py-[1px] rounded bg-gray-100 mt-0.5 text-gray-600 truncate w-full">
              {profile.profileType}
            </span>
          </div>
        </button>
      ))}
    </div>
  )}
</div>
)}

      {posts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="flex flex-col items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center mb-4">
              <FiMessageCircle className="w-10 h-10 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t("noPostsYet")}</h3>
            <p className="text-gray-500 text-sm max-w-sm">
              {activeTab === "my" ? t("noPostsMessageMy") : t("noPostsMessageAll")}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              currentUserId={currentUserId}
              activeTab={activeTab}
              likedPosts={likedPosts}
              comments={comments}
              newComment={newComment}
              expandedComments={expandedComments}
              currentMediaIndex={currentMediaIndex}
              editingPost={editingPost}
              showOptionsMenu={showOptionsMenu}
              posts={posts}
              onLike={onLike}
              onShowLikes={onShowLikes}
              onEdit={onEdit}
              onDelete={onDelete}
              onSavePost={onSavePost}
              onAddComment={onAddComment}
              onDeleteComment={onDeleteComment}
              onToggleComments={onToggleComments}
              onSetNewComment={onSetNewComment}
              onSetShowOptionsMenu={onSetShowOptionsMenu}
              onSetEditingPost={onSetEditingPost}
              onRemoveSingleMedia={onRemoveSingleMedia}
              onSetCurrentMediaIndex={onSetCurrentMediaIndex}
              onOpenReportModal={onOpenReportModal}
              onViewProfile={handleViewProfile}
              t={t}
            />
          ))}
        </div>
      )}
      <FeedsModals
        showLikesModal={showLikesModal}
        loadingLikes={loadingLikes}
        likesData={likesData}
        deleteModalPostId={deleteModalPostId}
        reportModalPostId={reportModalPostId}
        reportReason={reportReason}
        onCloseLikesModal={onCloseLikesModal}
        onCloseDeleteModal={onCloseDeleteModal}
        onConfirmDelete={onConfirmDelete}
        onCloseReportModal={onCloseReportModal}
        onSetReportReason={onSetReportReason}
        onConfirmReport={onConfirmReport}
        onViewProfile={handleViewProfile}
        t={t}
      />
    </div>
  );
}; 
